import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { getFirebase, FIREBASE_ENABLED } from './firebase';
import {
  signInWithPopup, signOut as firebaseSignOut,
} from 'firebase/auth';

const STORAGE_KEY = 'clt-session';

export type ProductRole = 'owner' | 'admin' | 'teamLead' | 'educator' | 'salesAssociate' | 'learner';

export interface Session {
  uid: string;
  role: ProductRole;
  email: string;
  name: string;
  initials: string;
  tenantId: string;
  tenantName: string;
  /** When this session was issued locally. Used to enforce re-auth after lock window. */
  issuedAt: number;
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (!s || !s.uid || !s.tenantId) return null;
    if (Date.now() - (s.issuedAt || 0) > 14 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return s;
  } catch { return null; }
}
export function setSession(s: Session | null) {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
}

export async function signOut() {
  if (FIREBASE_ENABLED) {
    const fb = await getFirebase();
    if (fb) try { await firebaseSignOut(fb.auth as Parameters<typeof firebaseSignOut>[0]); } catch { /* ignore */ }
  }
  setSession(null);
}

export interface TenantSettings {
  ownerEmail?: string;
  allowedEmails?: string[];
  allowedDomains?: string[];
  notifyUids?: string[];
}

async function readTenantSettings(tenantId: string): Promise<TenantSettings> {
  const fb = await getFirebase();
  if (!fb) return {};
  try {
    const snap = await getDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId));
    return snap.exists() ? (snap.data() as TenantSettings) : {};
  } catch { return {}; }
}

export async function setTenantSettings(tenantId: string, patch: Partial<TenantSettings>): Promise<void> {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not configured.');
  await setDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId), patch, { merge: true });
}

function pickDisplayName(raw: string): string {
  if (!raw) return '';
  const cleaned = raw.replace(/[._-]+/g, ' ').trim();
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
}

function makeInitials(name: string, email: string): string {
  const src = (name || email || '?').split(/\s+/).filter(Boolean);
  return (src[0]?.[0] || '?').toUpperCase() + (src[1]?.[0] || '').toUpperCase();
}

/**
 * Sign in via Google and gate on workspace whitelist.
 *
 * Flow:
 *   1) Google sign-in popup
 *   2) Read tenant settings (allowedEmails / allowedDomains / ownerEmail)
 *   3) Look up existing users/{uid} doc (silently treats permission-denied as "no membership"):
 *      - if exists → onboard with stored role
 *   4) Else if email matches tenant.ownerEmail → auto-create users/{uid} as 'owner'
 *   5) Else if email in whitelist OR domain in allowedDomains → create users/{uid} as 'learner'
 *   6) Else → return { needsRequestAccess: true } (caller shows the Request Access UI)
 */
export async function signInWithGoogle(tenantId: string): Promise<Session | { needsRequestAccess: true; email: string; name: string; uid: string }> {
  if (!FIREBASE_ENABLED) throw new Error('Sign-in is unavailable. Please contact your administrator.');
  const fb = await getFirebase();
  if (!fb) throw new Error('Sign-in is temporarily unavailable. Please try again in a moment.');

  const credential = await signInWithPopup(fb.auth as Parameters<typeof signInWithPopup>[0], fb.googleProvider as Parameters<typeof signInWithPopup>[1]);
  const user = credential.user;
  const email = (user.email || '').trim().toLowerCase();
  const displayName = user.displayName || pickDisplayName(email);

  const ref = doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'users', user.uid);

  // Try to read the existing membership doc. A permission-denied error here means
  // the user has no membership yet (the rules explicitly allow self-read, so this
  // is defensive in case the rule isn't deployed yet). Any other error propagates.
  let existing: { exists: () => boolean; data: () => Record<string, unknown> | undefined };
  try {
    existing = await getDoc(ref) as unknown as { exists: () => boolean; data: () => Record<string, unknown> | undefined };
  } catch (e: unknown) {
    const code = (e as { code?: string })?.code;
    const msg = (e instanceof Error ? e.message : String(e)) || '';
    if (code === 'permission-denied' || /permission|insufficient/i.test(msg)) {
      existing = { exists: () => false, data: () => undefined };
    } else {
      throw e;
    }
  }
  if (existing.exists()) {
    const data = existing.data() as { role?: ProductRole; name?: string; tenantName?: string; initials?: string } | undefined;
    const allowed: ProductRole[] = ['owner', 'admin', 'teamLead', 'educator', 'salesAssociate', 'learner'];
    if (!data || !data.role || !allowed.includes(data.role)) {
      await firebaseSignOut(fb.auth as Parameters<typeof firebaseSignOut>[0]);
      throw new Error('Your account has no valid role in this tenant. Ask the owner to assign one.');
    }
    const settings = await readTenantSettings(tenantId);
    const tenantName = data.tenantName || (settings as { name?: string }).name || tenantId;
    const session: Session = {
      uid: user.uid, role: data.role, email, name: data.name || displayName,
      initials: data.initials || makeInitials(displayName, email), tenantId, tenantName, issuedAt: Date.now(),
    };
    setSession(session);
    return session;
  }

  // New user — gate against the whitelist. readTenantSettings swallows its own errors.
  const settings = await readTenantSettings(tenantId);
  const allowedEmails = (settings.allowedEmails || []).map((e) => e.toLowerCase());
  const allowedDomains = (settings.allowedDomains || []).map((d) => d.toLowerCase());
  const ownerEmail = (settings.ownerEmail || '').toLowerCase();

  const isOwner = !!(ownerEmail && ownerEmail === email);
  const onDomain = allowedDomains.some((d) => d && email.endsWith('@' + d));
  const onList = isOwner || allowedEmails.includes(email) || onDomain;

  if (!onList) {
    // Do NOT sign out — keep the Google credential so the user can hit "Request access" with one click.
    return { needsRequestAccess: true, email, name: displayName, uid: user.uid };
  }

  if (isOwner) {
    // Owner auto-claim — create the owner membership doc.
    await setDoc(ref, {
      role: 'owner', email, name: displayName, initials: makeInitials(displayName, email),
      tenantId, ownerEmail, autoClaim: true, createdAt: new Date().toISOString(),
    }, { merge: true });
    const tenantName = (settings as { name?: string }).name || tenantId;
    const session: Session = {
      uid: user.uid, role: 'owner', email, name: displayName,
      initials: makeInitials(displayName, email), tenantId, tenantName, issuedAt: Date.now(),
    };
    setSession(session);
    return session;
  }

  // Whitelisted (not owner) — auto-create as learner.
  await setDoc(ref, {
    role: 'learner', email, name: displayName, initials: makeInitials(displayName, email),
    tenantId, allowedBy: 'whitelist', createdAt: new Date().toISOString(),
  }, { merge: true });
  const tenantName = (settings as { name?: string }).name || tenantId;
  const session: Session = {
    uid: user.uid, role: 'learner', email, name: displayName,
    initials: makeInitials(displayName, email), tenantId, tenantName, issuedAt: Date.now(),
  };
  setSession(session);
  return session;
}

// ---------------- Request access ----------------

export interface AccessRequest { name: string; email: string; uid: string; roleRequest?: ProductRole; note: string; status: 'pending' | 'approved' | 'denied'; createdAt: number; approvedBy?: string; approvedAt?: number; assignedRole?: ProductRole; }

export async function requestAccessFromGoogle(tenantId: string, uid: string, email: string, name: string, note: string): Promise<void> {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not configured.');
  await addDoc(collection(fb.db as Parameters<typeof collection>[0], 'tenants', tenantId, 'accessRequests'), {
    uid, email: email.trim().toLowerCase(), name: name.trim(), note: note || '',
    status: 'pending', createdAt: Date.now(),
  });
}

export async function listAccessRequests(tenantId: string): Promise<Array<AccessRequest & { id: string }>> {
  const fb = await getFirebase();
  if (!fb) return [];
  const snap = await getDocs(query(collection(fb.db as Parameters<typeof collection>[0], 'tenants', tenantId, 'accessRequests'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as AccessRequest) }));
}

export function subscribeAccessRequests(tenantId: string, cb: (rows: Array<AccessRequest & { id: string }>) => void): () => void {
  let unsub = () => {};
  getFirebase().then((fb) => {
    if (!fb) return;
    const q = query(collection(fb.db as Parameters<typeof collection>[0], 'tenants', tenantId, 'accessRequests'), orderBy('createdAt', 'desc'));
    unsub = onSnapshot(q, (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AccessRequest) })));
    });
  }).catch(() => {});
  return () => unsub();
}

/** Approve a request: creates users/{uid} doc + adds email to whitelist + marks request approved. */
export async function approveAccessRequest(tenantId: string, requestId: string, role: ProductRole): Promise<void> {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not configured.');
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role)) throw new Error('Only Owner/Admin can approve requests.');

  const reqRef = doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'accessRequests', requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error('Request not found.');
  const req = reqSnap.data() as AccessRequest;
  if (!req.email || !req.uid) throw new Error('Request is missing the Google account info.');

  await setDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'users', req.uid), {
    role, email: req.email.toLowerCase(), name: req.name,
    initials: makeInitials(req.name, req.email), tenantId,
    addedBy: session.uid, createdAt: new Date().toISOString(),
  }, { merge: true });

  const tenantRef = doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId);
  const tSnap = await getDoc(tenantRef);
  const settings = (tSnap.data() || {}) as TenantSettings;
  const allowed = Array.from(new Set([...(settings.allowedEmails || []), req.email.toLowerCase()]));
  await setDoc(tenantRef, { allowedEmails: allowed }, { merge: true });

  await setDoc(reqRef, {
    status: 'approved', assignedRole: role, approvedBy: session.uid, approvedAt: Date.now(),
  }, { merge: true });
}

export async function denyAccessRequest(tenantId: string, requestId: string): Promise<void> {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not configured.');
  const session = getSession();
  if (!session || !['owner', 'admin'].includes(session.role)) throw new Error('Only Owner/Admin can deny requests.');
  await setDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'accessRequests', requestId), {
    status: 'denied', deniedBy: session.uid, deniedAt: Date.now(),
  }, { merge: true });
}

// ---------------- Whitelist helpers (Owner/Admin only) ----------------

export async function addAllowedEmail(tenantId: string, email: string): Promise<void> {
  await setTenantSettings(tenantId, { allowedEmails: [email.trim().toLowerCase()] });
}
export async function removeAllowedEmail(tenantId: string, email: string): Promise<void> {
  const fb = await getFirebase();
  if (!fb) return;
  const ref = doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId);
  const snap = await getDoc(ref);
  const settings = (snap.data() || {}) as TenantSettings;
  const filtered = (settings.allowedEmails || []).filter((e) => e.toLowerCase() !== email.trim().toLowerCase());
  await setDoc(ref, { allowedEmails: filtered }, { merge: true });
}
export async function listAllowedEmails(tenantId: string): Promise<string[]> {
  const s = await readTenantSettings(tenantId);
  return s.allowedEmails || [];
}

// ---------------- Invite-link tokens (admin pre-approves a role on a link) ----------------

export interface InviteToken {
  code: string;
  role: ProductRole;
  note?: string;
  email?: string;
  used: boolean;
  usedBy?: string;
  usedAt?: number;
  expiresAt: number;
  createdAt: number;
  createdBy: string;
}

function randomToken(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 10 }, () => alphabet[Math.floor(Math.random() * 29)]).join('');
}

export async function generateInviteLink(tenantId: string, opts: { role: ProductRole; note?: string; email?: string; hours?: number }): Promise<{ code: string; url: string }> {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not configured.');
  const code = randomToken();
  const payload: InviteToken = {
    code, role: opts.role, note: opts.note || '', email: (opts.email || '').toLowerCase(),
    used: false, expiresAt: Date.now() + (opts.hours || 168) * 3600 * 1000,
    createdAt: Date.now(), createdBy: getSession()?.uid || '',
  };
  await setDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'invites', code), payload);
  const base = (typeof window !== 'undefined' && window.location ? window.location.origin : '') || 'https://clientale.in';
  return { code, url: `${base}/#/invite/${code}` };
}

export async function listInviteLinks(tenantId: string): Promise<InviteToken[]> {
  const fb = await getFirebase();
  if (!fb) return [];
  const snap = await getDocs(query(collection(fb.db as Parameters<typeof collection>[0], 'tenants', tenantId, 'invites'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => ({ code: d.id, ...(d.data() as Omit<InviteToken, 'code'>) }));
}

export async function consumeInviteLink(tenantId: string, code: string): Promise<InviteToken | null> {
  const fb = await getFirebase();
  if (!fb) return null;
  const c = code.trim().toUpperCase();
  const snap = await getDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'invites', c));
  if (!snap.exists()) return null;
  const t = snap.data() as InviteToken;
  if (t.used) return null;
  if (Date.now() > t.expiresAt) return null;
  return { code: c, ...t };
}

export async function markInviteUsed(tenantId: string, code: string, uid: string): Promise<void> {
  const fb = await getFirebase();
  if (!fb) return;
  await setDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'invites', code.trim().toUpperCase()),
    { used: true, usedBy: uid, usedAt: Date.now() }, { merge: true });
}

export async function applyInviteOnSignIn(tenantId: string, uid: string, email: string, displayName: string, token: InviteToken): Promise<Session> {
  const fb = await getFirebase();
  if (!fb) throw new Error('Firebase is not configured.');
  if (token.email && token.email !== email.toLowerCase()) throw new Error('This invite link was sent to a different email.');
  const userRef = doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'users', uid);
  await setDoc(userRef, {
    role: token.role, email, name: displayName,
    initials: makeInitials(displayName, email), tenantId,
    inviteCode: token.code, addedBy: token.createdBy || 'invite', createdAt: new Date().toISOString(),
  }, { merge: true });
  const tRef = doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId);
  const settings = ((await getDoc(tRef)).data() || {}) as TenantSettings;
  const allowed = Array.from(new Set([...(settings.allowedEmails || []), email.toLowerCase()]));
  await setDoc(tRef, { allowedEmails: allowed }, { merge: true });
  await markInviteUsed(tenantId, token.code, uid);

  const tenantName = (settings as { name?: string }).name || tenantId;
  const session: Session = {
    uid, role: token.role, email, name: displayName,
    initials: makeInitials(displayName, email), tenantId, tenantName, issuedAt: Date.now(),
  };
  setSession(session);
  return session;
}

// ---------------- Legacy helpers kept for backward compatibility ----------------

export async function listMemberships(tenantId: string): Promise<Array<{ id: string; role: ProductRole; email?: string; name?: string }>> {
  const fb = await getFirebase();
  if (!fb) return [];
  const snap = await getDocs(collection(fb.db as Parameters<typeof collection>[0], 'tenants', tenantId, 'users'));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as { role: ProductRole; email?: string; name?: string }) }));
}
export async function findMembershipByEmail(email: string, tenantId: string): Promise<{ id: string; role: ProductRole } | null> {
  const fb = await getFirebase();
  if (!fb) return null;
  const snap = await getDocs(query(collection(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'users'), where('email', '==', email.toLowerCase())));
  if (!snap.docs.length) return null;
  const d = snap.docs[0];
  return { id: d.id, role: (d.data() as { role: ProductRole }).role };
}
export async function promoteSelfToOwner(tenantId: string): Promise<void> {
  const session = getSession();
  if (!session) throw new Error('Sign in first.');
  const fb = await getFirebase();
  if (!fb) return;
  await setDoc(doc(fb.db as Parameters<typeof doc>[0], 'tenants', tenantId, 'users', session.uid), { role: 'owner', tenantId }, { merge: true });
  session.role = 'owner';
  setSession(session);
}
export async function generateInvite(tenantId: string, opts: { email: string; role: ProductRole; note?: string; hours?: number }): Promise<string> {
  const r = await generateInviteLink(tenantId, { ...opts, email: opts.email });
  return r.code;
}
export async function listInvites(tenantId: string): Promise<Array<{ code: string; role: string; email?: string; used: boolean }>> {
  const rows = await listInviteLinks(tenantId);
  return rows.map((r) => ({ code: r.code, role: r.role, email: r.email, used: r.used }));
}
// 'noop' to silence unused-warning for the legacy helper.
void deleteDoc;